import { ReactElement } from "react"
import { Control } from "react-hook-form"

import {
    NETWORK_TO_DNO_ABBREVIATION_DISPATCHER,
    NIE_ID,
} from "../../../../constants/dnos"
import {
    BELFAST_POST_CODE_AREA,
    METER_NUMBER_KW,
    METER_NUMBER_LABEL,
} from "../../../../constants/generalConstants"
import {
    TT_AMENDMENT_MODE_DISABLED,
    TT_METER_NUMBER,
    TT_MPAN,
    TT_MPRN,
} from "../../../../constants/tooltips"
import { validateUkMpan } from "../../../../utils/mpan/mpanUtils"
import ValidatedTextField from "../../../inputs/validated/validatedTextField"
import DisplayIf from "../../../wrappers/displayIf"

interface MpanRecognisedInputProps {
    registerName: string
    control: Control
    postCode: string
    isMpanEntered: boolean
    isMeterNumberEntered: boolean
    forceShowMpanInput?: boolean
    customValidation?: (value: string) => boolean | string
    disabled?: boolean
    isExportMpan?: boolean
    applicationId?: string
}

const NIE_MPRN_LENGTH = 11
const GB_MPAN_LENGTH = 13

/**
 * Recognised input for MPAN number. Adds custom validation of MPAN. For
 * Northern Irish postcodes, the input is for MPRN and there is an additional
 * input for meter number.
 */
const MpanRecognisedInput = ({
    registerName,
    control,
    postCode,
    isMpanEntered,
    isMeterNumberEntered,
    forceShowMpanInput,
    customValidation,
    disabled = false,
    isExportMpan = false,
    applicationId,
}: MpanRecognisedInputProps): ReactElement => {
    const isNie = String(postCode)
        .toLowerCase()
        .startsWith(BELFAST_POST_CODE_AREA)
    const label = isNie ? "MPRN" : "MPAN"
    const placeholder = isNie ? "Enter MPRN..." : "Enter MPAN..."
    const tooltip = isNie ? TT_MPRN : TT_MPAN
    const mpanLength = isNie ? NIE_MPRN_LENGTH : GB_MPAN_LENGTH
    const mpanlengthMsg = isNie
        ? "MPRN must be 11 characters long"
        : "MPAN must be 13 characters long"

    const dnoIds = isNie
        ? [NIE_ID]
        : Object.keys(NETWORK_TO_DNO_ABBREVIATION_DISPATCHER).filter(
              (key) => key !== NIE_ID
          )

    const isNeitherEntered = !isMpanEntered && !isMeterNumberEntered

    return (
        <>
            <DisplayIf condition={isNie || !!forceShowMpanInput}>
                <ValidatedTextField
                    registerName={registerName}
                    control={control}
                    disabled={disabled}
                    required={!isNie}
                    label={label}
                    placeholder={placeholder}
                    customRules={{
                        validate: {
                            validate: (value) => {
                                // If adding a new validation rule that returns `True`, make sure to update the
                                // is numeric check
                                if (
                                    (isNeitherEntered && !value) ||
                                    (!isNie && !value)
                                ) {
                                    return isNie
                                        ? "Either MPRN or meter number is required for Northern Ireland Installations"
                                        : "This field is required"
                                }

                                if (!value && isNie && isMeterNumberEntered) {
                                    return true
                                }

                                const startsWithValidKey = dnoIds.some((key) =>
                                    value.startsWith(key)
                                )
                                if (!startsWithValidKey && !isNie) {
                                    return "Invalid value. First two digits must match a GB DNO license area ID"
                                }

                                if (isNie && !value.startsWith(NIE_ID)) {
                                    return (
                                        `Invalid MPRN. This value must start with ${NIE_ID} ` +
                                        "as you have entered a Northern Irish postcode"
                                    )
                                }

                                if (
                                    Number.isNaN(Number(value)) ||
                                    !Number.isInteger(Number(value))
                                ) {
                                    return "This value must be numeric"
                                }
                                // ONLY perform this check if the MPAN is UK based
                                if (
                                    !isNie &&
                                    !validateUkMpan(value.toString())
                                ) {
                                    return "Invalid MPAN Entered."
                                }

                                // Export MPAN validation: check first two digits match application ID
                                if (isExportMpan && applicationId && !isNie) {
                                    const exportMpanPrefix = value.substring(
                                        0,
                                        2
                                    )
                                    const applicationIdPrefix =
                                        applicationId.substring(0, 2)

                                    if (
                                        exportMpanPrefix !== applicationIdPrefix
                                    ) {
                                        return "Export MPAN must start with the same two digits as the application MPAN"
                                    }
                                }

                                if (customValidation) {
                                    const customValidationResult =
                                        customValidation(value)

                                    if (customValidationResult === false) {
                                        return "This value is invalid"
                                    }

                                    if (
                                        typeof customValidationResult ===
                                        "string"
                                    ) {
                                        return customValidationResult
                                    }
                                }

                                return true
                            },
                        },
                        minLength: {
                            value: mpanLength,
                            message: mpanlengthMsg,
                        },
                        maxLength: {
                            value: mpanLength,
                            message: mpanlengthMsg,
                        },
                    }}
                    tooltip={`${
                        disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                    }${tooltip}`}
                />
            </DisplayIf>
            <DisplayIf condition={isNie}>
                <ValidatedTextField
                    registerName={METER_NUMBER_KW}
                    control={control}
                    disabled={disabled}
                    required={false}
                    customRules={{
                        validate: {
                            validate: (value) => {
                                if (isNeitherEntered) {
                                    return true
                                }

                                if (!value && isMpanEntered) {
                                    return true
                                }

                                if (!isMpanEntered && !value) {
                                    return "Either MPRN or meter number is required for Northern Ireland Installations"
                                }

                                return true
                            },
                        },
                    }}
                    label={METER_NUMBER_LABEL}
                    placeholder="Enter meter number..."
                    tooltip={`${
                        disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                    }${TT_METER_NUMBER}`}
                />
            </DisplayIf>
        </>
    )
}

MpanRecognisedInput.defaultProps = {
    customValidation: undefined,
    disabled: false,
    forceShowMpanInput: false,
    isExportMpan: false,
    applicationId: undefined,
}

export default MpanRecognisedInput
